import { atom } from "jotai";
import {
  PlantService,
  FileService,
} from "../../services/staffandplant/plantService";

// Base plant atom to store all plant data
export const plantAtom = atom({
  projectPlants: [],
  orgPlants: [],
  loading: false,
  error: null,
});

// Derived atom for UI state
export const plantUIAtom = atom({
  activeTab: 0, // 0 for project plants, 1 for org plants
  searchQuery: "",
  selectedPlants: [],
  filteredPlants: [],
});

// Process plant documents to include file URLs
const processPlantDocuments = async (plants) => {
  if (!Array.isArray(plants)) return [];

  for (const plant of plants) {
    const documents = plant.documents || plant.document || [];
    plant.documents = documents; // Standardize to `documents`
  }
  return plants;
};

// Fetch project plants (only_org=False or dedicated endpoint)
export const fetchProjectPlantsAtom = atom(null, async (get, set) => {
  set(plantAtom, (prev) => ({ ...prev, loading: true, error: null }));

  try {
    const plants = await PlantService.fetchProjectPlants();
    const processedPlants = await processPlantDocuments(plants);

    set(plantAtom, (prev) => ({
      ...prev,
      projectPlants: processedPlants,
      loading: false,
    }));

    return processedPlants;
  } catch (error) {
    set(plantAtom, (prev) => ({
      ...prev,
      loading: false,
      error: error.message,
    }));
    throw error;
  }
});

// Fetch organization plants (only_org=True)
export const fetchOrgPlantsAtom = atom(null, async (get, set) => {
  set(plantAtom, (prev) => ({ ...prev, loading: true, error: null }));

  try {
    const plants = await PlantService.fetchPlants(true);
    const processedPlants = await processPlantDocuments(plants);

    set(plantAtom, (prev) => ({
      ...prev,
      orgPlants: processedPlants,
      loading: false,
    }));

    return processedPlants;
  } catch (error) {
    set(plantAtom, (prev) => ({
      ...prev,
      loading: false,
      error: error.message,
    }));
    throw error;
  }
});

// Fetch document URL for a specific document
export const fetchDocumentUrlAtom = atom(null, async (get, set, docId) => {
  try {
    const fileUrl = await FileService.getFileUrl(docId, true);
    return fileUrl;
  } catch (error) {
    console.error(`Error fetching document URL for ${docId}:`, error);
    return null;
  }
});

// Create new plant
export const createPlantAtom = atom(null, async (get, set, plantData) => {
  set(plantAtom, (prev) => ({ ...prev, loading: true, error: null }));

  try {
    const result = await PlantService.createPlant(plantData);

    // Refresh org plants after creation
    await set(fetchOrgPlantsAtom);

    return result;
  } catch (error) {
    set(plantAtom, (prev) => ({
      ...prev,
      loading: false,
      error: error.message,
    }));
    throw error;
  }
});

// Update existing plant
export const updatePlantAtom = atom(
  null,
  async (get, set, { plantId, plantData }) => {
    set(plantAtom, (prev) => ({ ...prev, loading: true, error: null }));

    try {
      const result = await PlantService.updatePlant(plantId, plantData);

      // Refresh the appropriate plant list based on current context
      const currentState = get(plantUIAtom);
      if (currentState.activeTab === 0) {
        await set(fetchProjectPlantsAtom);
      } else {
        await set(fetchOrgPlantsAtom);
      }

      return result;
    } catch (error) {
      set(plantAtom, (prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
      throw error;
    }
  }
);

// Delete plant
export const deletePlantAtom = atom(null, async (get, set, plantId) => {
  set(plantAtom, (prev) => ({ ...prev, loading: true, error: null }));

  try {
    const result = await PlantService.deletePlant(plantId);

    // Refresh org plants after deletion
    await set(fetchOrgPlantsAtom);

    return result;
  } catch (error) {
    set(plantAtom, (prev) => ({
      ...prev,
      loading: false,
      error: error.message,
    }));
    throw error;
  }
});

// Deallocate plant from project
export const deallocatePlantAtom = atom(null, async (get, set, plantId) => {
  set(plantAtom, (prev) => ({ ...prev, loading: true, error: null }));

  try {
    const result = await PlantService.deallocatePlant(plantId);

    // Refresh project plants after deallocation
    await set(fetchProjectPlantsAtom);

    return result;
  } catch (error) {
    set(plantAtom, (prev) => ({
      ...prev,
      loading: false,
      error: error.message,
    }));
    throw error;
  }
});

// Import plants to project
export const importPlantsAtom = atom(null, async (get, set, plantIds) => {
  set(plantAtom, (prev) => ({ ...prev, loading: true, error: null }));

  try {
    const result = await PlantService.importPlants(plantIds);

    // Refresh project plants after import
    await set(fetchProjectPlantsAtom);

    return result;
  } catch (error) {
    set(plantAtom, (prev) => ({
      ...prev,
      loading: false,
      error: error.message,
    }));
    throw error;
  }
});

// Upload files atom
export const uploadFilesAtom = atom(null, async (get, set, files) => {
  try {
    const results = await FileService.uploadMultipleFiles(files, true);
    return results;
  } catch (error) {
    console.error("Error uploading files:", error);
    throw error;
  }
});

// Utility atom to get current plants based on active tab
export const currentPlantsAtom = atom((get) => {
  const plantState = get(plantAtom);
  const uiState = get(plantUIAtom);

  const plants =
    uiState.activeTab === 0 ? plantState.projectPlants : plantState.orgPlants;

  // Apply search filter
  if (!uiState.searchQuery) return plants;

  const searchLower = uiState.searchQuery.toLowerCase();
  return plants.filter((plant) => {
    return (
      (plant.nickname || plant.name || "")
        .toLowerCase()
        .includes(searchLower) ||
      (plant.plant_id || "").toLowerCase().includes(searchLower) ||
      (plant.plant_type || "").toLowerCase().includes(searchLower) ||
      (plant.make || "").toLowerCase().includes(searchLower) ||
      (plant.model || "").toLowerCase().includes(searchLower) ||
      (plant.registration_number || "").toLowerCase().includes(searchLower)
    );
  });
});
